import * as React from 'react';
import {useDispatch} from 'react-redux';
import isEqual from 'lodash/isEqual';
import {
  hashKey,
  keepPreviousData,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import {baseRequest} from 'src/utils/redux-api-v2';
import {WIDGET_TYPE} from 'src/types/byod';
import type {
  ByodFilterMap,
  DashboardQueryResponse,
  PreviewResponse,
  QueryWidgetInput,
  TileFetchStatus,
  WidgetConfig,
} from 'src/types/byod';

import {useInViewport} from 'src/hooks/useInViewport';
import {BYOD_API_BASE as BASE} from 'src/components/analytics-genesis/byod/api';
import {DEFAULT_PAGE_SIZE} from 'src/components/analytics-genesis/byod/constants';
import {normalizePreviewResponse} from 'src/components/analytics-genesis/byod/utils/normalize-preview-response';
import {
  BYODTileError,
  isAbortOrCancelError,
  isByodErrorRetryable,
  reportByodApiError,
} from 'src/components/analytics-genesis/byod/api/error-handling';

/** Default number of automatic retries before the failure lock kicks in. */
const DEFAULT_TILE_RETRY = 1;

export type UseTileDataOptions = {
  /**
   * Number of automatic retries on transient errors before the tile locks
   * itself out of further auto-fetches. Defaults to 1 (so up to 2 total
   * attempts per fetch sequence).
   */
  retry?: number;
};

/**
 * True when the consuming tile is being rendered inside a drag overlay (a
 * transient, cursor-following clone) rather than its real canvas slot. Read via
 * `useContext` so deep children don't need a prop drilled through. Used to
 * suppress the viewport-driven fetch — the overlay clone shares the placementId
 * of an already-loaded tile.
 */
const ByodDragOverlayContext = React.createContext(false);

export const ByodDragOverlayProvider = ByodDragOverlayContext.Provider;

/** Query key namespace — used for scoped invalidation (`['byod-tile']`). */
export const BYOD_TILE_QUERY_KEY = 'byod-tile';

export type UseTileDataResult = {
  /** Ref to attach to the tile wrapper — drives visibility tracking. */
  ref: (el: Element | null) => void;
  /** Widget data payload once loaded. */
  data: PreviewResponse | undefined;
  /** True from first enabled fetch until either success or error. */
  isLoading: boolean;
  /** True while a request is in flight (initial or refetch). */
  isFetching: boolean;
  isError: boolean;
  error: Error | null;
  /** React Query's fetch lifecycle state (see `TileFetchStatus`). */
  fetchStatus: TileFetchStatus;
  /** Manual retry (used by tile-level retry buttons). */
  refetch: () => void;
};

/**
 * Assemble the wire payload for `/query`. Takes pre-resolved `filters` (the
 * caller owns filter resolution), strips display-only config keys the backend
 * ignores, and stamps table pagination defaults.
 *
 * Exported for unit testing — `useTileData` is the only runtime caller.
 */
export function buildQueryInput(
  placementId: string,
  widget: WidgetConfig | undefined,
  filters: ByodFilterMap,
): QueryWidgetInput | null {
  if (!widget) {
    return null;
  }

  const {
    chart_style: _cs,
    x_axis_keys: _xk,
    y_axis_keys: _yk,
    selected_columns: _sc,
    ...widgetConfig
  } = (widget.widget_config_json ?? {}) as Record<string, unknown>;

  // Tables need pagination on the very first request — ByodTable skips its
  // initial-mount onFetchData call, so stamp defaults at the API boundary
  // rather than polluting widget_config_json.
  if (widget.widget_type === WIDGET_TYPE.TABLE) {
    if (widgetConfig.limit == null) {
      widgetConfig.limit = DEFAULT_PAGE_SIZE;
    }
    if (widgetConfig.offset == null) {
      widgetConfig.offset = 0;
    }
  }

  return {
    placement_id: placementId,
    widget_key: widget.widget_key,
    widget_filters: filters,
    widget_config: widgetConfig,
  };
}

/**
 * Build a `BYODTileError`, report it to Sentry, and throw it — the single
 * failure path for every terminal error inside the tile `queryFn`. Returns
 * `never`, so callers don't need a trailing `throw`. `reportByodApiError`
 * defers to the Sentry wrappers (which swallow their own failures), so no
 * defensive try/catch around the report is needed.
 */
function throwTileError(
  placementId: string,
  message: string,
  retryable: boolean,
): never {
  const err = new BYODTileError(message, {retryable});
  reportByodApiError(err, {action: 'tile-query', placementId});
  throw err;
}

/**
 * Validate a `/query` response for a single tile and return its normalized
 * payload. Throws (via `throwTileError`, so it's reported) on a malformed wire
 * shape, an empty response, or a backend-rejected tile — never returns a
 * fallback that would mask a failure. Exported/pure so the error branches are
 * unit tested without rendering the hook.
 *
 * `response` is typed as possibly `undefined` on purpose: a 200 carrying an
 * error envelope, a proxy/HTML error page that deserialized oddly, or a contract
 * drift can leave `widgets` missing. Guarding the shape here routes those
 * through `throwTileError` instead of letting `response.widgets[0]` throw a raw,
 * unreported `TypeError`.
 */
export function parseTileQueryResponse(
  placementId: string,
  response: DashboardQueryResponse | undefined,
): PreviewResponse {
  const widgets = response?.widgets;
  if (!Array.isArray(widgets)) {
    throwTileError(placementId, 'Malformed response from /query', true);
  }
  const result = widgets[0];
  if (!result) {
    throwTileError(placementId, 'Empty response from /query', true);
  }
  if (result.status === 'error') {
    // Backend explicitly rejected this tile's query — the most diagnostic
    // tile failure.
    throwTileError(placementId, result.error || 'Tile query failed', false);
  }
  const {placement_id: _p, status: _s, ...payload} = result;
  return normalizePreviewResponse(payload) as PreviewResponse;
}

/**
 * Whether the tile's `/query` should be enabled. A tile fetches only when it's
 * visible, has a widget to render, isn't failure-locked, and isn't a drag-overlay
 * clone. Extracted as a pure function so the (easy-to-break) gate can be unit
 * tested without rendering the hook.
 */
export function computeTileFetchEnabled(params: {
  isInViewport: boolean;
  hasWidget: boolean;
  isLocked: boolean;
  isDragOverlay: boolean;
}): boolean {
  return (
    params.isInViewport &&
    params.hasWidget &&
    !params.isLocked &&
    !params.isDragOverlay
  );
}

/**
 * Whether to engage the per-key failure lock. Locks ONLY on a genuine
 * fetching→settled transition that ended in error (`wasFetching && !isFetching
 * && isError`) — never on a cached-error revisit (no fetch happened, so
 * `wasFetching` is false) and never mid-flight. Extracted pure so this subtle
 * three-way condition is unit tested directly.
 */
export function shouldEngageFailureLock(params: {
  wasFetching: boolean;
  isFetching: boolean;
  isError: boolean;
}): boolean {
  return params.wasFetching && !params.isFetching && params.isError;
}

export type UseTileDataParams = UseTileDataOptions & {
  /** Placement id — cache keying + the "widget missing" guard. */
  placementId: string;
  /** Widget to render. `undefined` disables the query (no fetch). */
  widget: WidgetConfig | undefined;
  /** Dashboard id for cache keying. May be `null` for unsaved drafts. */
  dashboardId: string | null;
  /**
   * Fully-resolved `widget_filters` (the tile's saved filters + the injected
   * canvas filters: the date window for the builder, `globalPayload` for the
   * embed). STORE-FREE — the caller (the controlled `<Canvas>` tile) owns
   * resolution, so the builder and embed share this one fetch path. Values may
   * be flat (string / list) or structured objects passed through opaquely.
   */
  filters: ByodFilterMap;
};

/**
 * Per-tile data fetch gated by viewport visibility. Store-free: widget,
 * dashboardId, and resolved filters arrive as params, so the SAME hook serves
 * the builder and the embeddable view.
 *
 * Fires `/query` for this placement only when the attached element has been
 * continuously visible (via `useInViewport`). In-flight requests are cancelled
 * if the tile scrolls out of view or the key changes; loaded data is cached
 * until invalidated. A per-key failure lock prevents hammering a backend that
 * already rejected this exact request.
 */
export function useTileData({
  placementId,
  widget,
  dashboardId,
  filters,
  retry,
}: UseTileDataParams): UseTileDataResult {
  const retryCount = retry ?? DEFAULT_TILE_RETRY;
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const {ref, isInViewport} = useInViewport({
    settleMs: 100,
    rootMargin: '200px 0px',
  });

  // Stable content-based reference for the resolved filters: same identity when
  // the shallow value is unchanged, so React Query reuses cached data when
  // filters toggle back (A → B → A hits cache).
  const filtersRef = React.useRef(filters);
  const stableFilters = React.useMemo(() => {
    if (isEqual(filtersRef.current, filters)) {
      return filtersRef.current;
    }
    filtersRef.current = filters;
    return filters;
  }, [filters]);

  // Exclude display-only fields from the query key — they change how a tile
  // renders but not the `/query` response.
  const queryRelevantConfig = React.useMemo(() => {
    const raw = widget?.widget_config_json;
    if (!raw) {
      return null;
    }
    const {
      chart_style: _cs,
      x_axis_keys: _xk,
      y_axis_keys: _yk,
      selected_columns: _sc,
      ...rest
    } = raw as Record<string, unknown>;
    return rest;
  }, [widget?.widget_config_json]);

  const widgetConfigRef = React.useRef(queryRelevantConfig);
  const stableWidgetConfig = React.useMemo(() => {
    if (isEqual(widgetConfigRef.current, queryRelevantConfig)) {
      return widgetConfigRef.current;
    }
    widgetConfigRef.current = queryRelevantConfig;
    return queryRelevantConfig;
  }, [queryRelevantConfig]);

  const queryKey = React.useMemo(
    () => [
      BYOD_TILE_QUERY_KEY,
      placementId,
      dashboardId,
      stableFilters,
      stableWidgetConfig,
    ],
    [placementId, dashboardId, stableFilters, stableWidgetConfig],
  );

  // Once a fetch sequence terminally fails for a queryKey, suppress auto-fetches
  // for that exact key until it changes or the user manually retries — viewport
  // churn shouldn't hammer a backend already rejecting this request. The lock id
  // is React Query's own key hash, so it always tracks the cache identity.
  const failedKeyHashRef = React.useRef<string | null>(null);
  const queryKeyHash = hashKey(queryKey);
  const isLocked = failedKeyHashRef.current === queryKeyHash;

  // Suppress fetching inside a drag overlay — the clone shares its placementId
  // with a mounted tile, so the cache already holds (or is fetching) this data.
  const isDragOverlay = React.useContext(ByodDragOverlayContext);

  const enabled = computeTileFetchEnabled({
    isInViewport,
    hasWidget: Boolean(widget),
    isLocked,
    isDragOverlay,
  });

  const query = useQuery<PreviewResponse, Error>({
    queryKey,
    enabled,
    // Never auto-refetch once a tile has loaded: its data stays fresh forever,
    // so scroll-out/scroll-in, remounts, and reconnects reuse the cache. A new
    // fetch happens ONLY when the queryKey changes (filters, query-relevant
    // widget config, or the injected date window) or on an explicit manual
    // retry. `refetchOnWindowFocus` is already off globally.
    staleTime: Infinity,
    placeholderData: keepPreviousData,
    retry: (failureCount, error) =>
      failureCount < retryCount && isByodErrorRetryable(error),
    retryDelay: (attempt: number) => Math.min(1000 * 2 ** attempt, 5000),
    queryFn: async ({signal}) => {
      const input = buildQueryInput(placementId, widget, stableFilters);
      if (!input) {
        // Mutation race: widget removed between enable check and fetch.
        throwTileError(placementId, 'Widget not found', false);
      }
      try {
        const response = (await dispatch(
          baseRequest(`${BASE}/query`, {
            method: 'POST',
            data: {widgets: [input]},
            signal,
            options: {handleAuth: true},
          }),
        )) as DashboardQueryResponse | undefined;
        return parseTileQueryResponse(placementId, response);
      } catch (error) {
        // throwTileError already reported BYODTileErrors, and aborts/cancels are
        // expected control flow. Report everything else — a raw ApiError from
        // the request, or a throw from normalizePreviewResponse on an
        // unexpected payload — so a real backend/contract break is never
        // silently swallowed (there's no global QueryCache.onError to catch it).
        if (!(error instanceof BYODTileError) && !isAbortOrCancelError(error)) {
          reportByodApiError(error, {action: 'tile-query', placementId});
        }
        throw error;
      }
    },
  });

  // Cancel the in-flight request only on unmount / key change — NOT on
  // scroll-out. Scrolling a tile out of view merely disables the query (via
  // `enabled`); a fetch already in flight is allowed to finish and populate the
  // cache, so scrolling back is an instant cache hit (staleTime Infinity)
  // instead of a refetch. `useInViewport`'s settle delay + rootMargin prevent
  // fling-scrolling from firing requests, so there's nothing wasteful to abort
  // here — cancelling a started fetch just discards work we're about to want
  // back, which is what made revisited tiles flash loading again.
  //
  // Guard on observer count: several embeds (or an embed + the builder) can
  // mount the SAME tile with an identical queryKey. Without the guard, one tile
  // unmounting would `cancelQueries` and abort the fetch a sibling still wants.
  // Only cancel when this is the last observer of the key.
  const cancelIfSoleObserver = React.useCallback(() => {
    const observers =
      queryClient
        .getQueryCache()
        .find({queryKey, exact: true})
        ?.getObserversCount() ?? 0;
    if (observers <= 1) {
      void queryClient.cancelQueries({queryKey});
    }
  }, [queryClient, queryKey]);

  React.useEffect(
    () => () => {
      cancelIfSoleObserver();
    },
    [cancelIfSoleObserver],
  );

  // Engage the lock only on a fresh fetch (fetching → non-fetching) that ends in
  // error — not on a cached-error revisit. Reset whenever the key changes.
  const wasFetchingRef = React.useRef(false);
  React.useEffect(() => {
    failedKeyHashRef.current = null;
    wasFetchingRef.current = false;
  }, [queryKeyHash]);

  React.useEffect(() => {
    const wasFetching = wasFetchingRef.current;
    const isFetching = query.fetchStatus === 'fetching';
    if (
      shouldEngageFailureLock({wasFetching, isFetching, isError: query.isError})
    ) {
      failedKeyHashRef.current = queryKeyHash;
    }
    wasFetchingRef.current = isFetching;
  }, [query.fetchStatus, query.isError, queryKeyHash]);

  const queryRefetchRef = React.useRef(query.refetch);
  queryRefetchRef.current = query.refetch;
  const refetch = React.useCallback(() => {
    failedKeyHashRef.current = null;
    void queryRefetchRef.current();
  }, []);

  return {
    ref,
    data: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: (query.error as Error | null) ?? null,
    fetchStatus: query.fetchStatus,
    refetch,
  };
}
